import { generateTimeOptions, DaysOfWeekInOrder, compareLabels } from '@helpers/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { CityDto, ZoneDto } from '@interfaces/types';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DayOfWeek } from '@helpers/enums';
import { useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import { z } from 'zod';

import { useServicesFormatter } from '../../hooks';

export const parkingConfigurationFormDefaultValues = {
  unlimitedTime: true,
  timeFrom: '08',
  timeTo: '20',
  cities: [],
  zones: [],
  days: [],
};

const parkingConfigurationFormSchema = (t: TFunction) =>
  z
    .object({
      cities: z.array(z.string()).min(1, t('COMPANY_POLICY.PARKING_CONFIGURATION_CITIES_REQUIRED')),
      zones: z.array(z.string()),
      days: z
        .array(z.nativeEnum(DayOfWeek))
        .min(1, t('COMPANY_POLICY.PARKING_CONFIGURATION_DAYS_REQUIRED')),
      timeFrom: z.string().optional().nullable(),
      timeTo: z.string().optional().nullable(),
      unlimitedTime: z.boolean({ coerce: true }),
    })
    .superRefine((data, ctx) => {
      if (!data.unlimitedTime) {
        if (!data.timeFrom) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('FORMS.REQUIRED'),
            path: ['timeFrom'],
          });
        }

        if (!data.timeTo) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('FORMS.REQUIRED'),
            path: ['timeTo'],
          });
        }

        if (data.timeFrom && data.timeTo && data.timeFrom >= data.timeTo) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('COMPANY_POLICY.PARKING_CONFIGURATION_TIME_INVALID'),
            path: ['timeTo'],
          });
        }
      }
    });

export type ParkingConfigurationFormData = z.infer<
  ReturnType<typeof parkingConfigurationFormSchema>
>;

interface UseParkingConfigurationFormProps {
  defaultValues: ParkingConfigurationFormData;
  cities: CityDto[];
  days: DayOfWeek[];
  zones: ZoneDto[];
}

export const useParkingConfigurationForm = (props: UseParkingConfigurationFormProps) => {
  const { defaultValues, cities, days, zones } = props;

  const { formatCities, formatDays, formatZones } = useServicesFormatter();
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(parkingConfigurationFormSchema(t)),
    defaultValues: defaultValues ?? parkingConfigurationFormDefaultValues,
    mode: 'onBlur',
  });

  const unlimitedTimeSelected = form.watch('unlimitedTime');
  const formSelectedTimeFrom = form.watch('timeFrom');
  const formSelectedCities = form.watch('cities');
  const formSelectedZones = form.watch('zones');
  const formSelectedTimeTo = form.watch('timeTo');
  const formSelectedDays = form.watch('days');

  const selectedCitiesCustomValue = formatCities(formSelectedCities, cities);
  const selectedDaysCustomValue = formatDays(formSelectedDays as DayOfWeek[]);
  const selectedZonesCustomValue = formatZones(
    formSelectedCities,
    formSelectedZones,
    cities,
    zones,
  );

  const [selectedCitiesCodes, setSelectedCitiesCodes] = useState<string[]>(formSelectedCities);

  const citiesOptions = useMemo(() => {
    return cities.map((city) => ({
      label: city.name,
      value: city.code,
    }));
  }, [cities]);

  const daysOptions = useMemo(() => {
    return days.map((day) => ({
      label: t(`DAYS.${day}`),
      value: day,
    }));
  }, [days, t]);

  const selectedCitiesOptions = useMemo(() => {
    return citiesOptions.filter(({ value }) => formSelectedCities.includes(value));
  }, [citiesOptions, formSelectedCities]);

  const zonesOptions = useMemo(() => {
    return zones
      .filter(({ cityCode }) => formSelectedCities.includes(cityCode))
      .map((zone) => ({
        node: zone.cityCode,
        label: zone.name,
        value: zone.code,
        type: zone.type,
      }));
  }, [formSelectedCities, zones]);

  const timeFromOptions = useMemo(
    () =>
      generateTimeOptions().map((option) => ({
        ...option,
        disabled: formSelectedTimeTo ? option.value >= formSelectedTimeTo : false,
      })),
    [formSelectedTimeTo],
  );

  const timeToOptions = useMemo(
    () =>
      generateTimeOptions().map((option) => ({
        ...option,
        disabled: formSelectedTimeFrom ? option.value <= formSelectedTimeFrom : false,
      })),
    [formSelectedTimeFrom],
  );

  const sortCities = (selectedValues: string[]) => {
    return selectedValues.sort((a, b) => {
      const cityA = citiesOptions.find((city) => city.value === a);
      const cityB = citiesOptions.find((city) => city.value === b);

      if (cityA && cityB) {
        return compareLabels(cityA, cityB);
      }
      return 0;
    });
  };

  const sortDays = (selectedValues: string[]) => {
    return selectedValues.sort(
      (a, b) =>
        DaysOfWeekInOrder.indexOf(a as DayOfWeek) - DaysOfWeekInOrder.indexOf(b as DayOfWeek),
    );
  };

  // This is for manipulating selected zones in case when selected cities are changed
  useEffect(() => {
    const addedCities = formSelectedCities.filter(
      (cityCode) => !selectedCitiesCodes.includes(cityCode),
    );
    const zonesToRemove = zones
      .filter(({ cityCode }) => !formSelectedCities.includes(cityCode))
      .map(({ code }) => code);
    const zonesToAdd = zones
      .filter(({ cityCode }) => addedCities.includes(cityCode))
      .map(({ code }) => code);
    const selectedZones = new Set([
      ...formSelectedZones.filter((zone) => !zonesToRemove.includes(zone)),
      ...zonesToAdd,
    ]);
    form.setValue('zones', Array.from(selectedZones));
    setSelectedCitiesCodes(formSelectedCities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSelectedCities, zones, form]);

  useEffect(() => {
    if (unlimitedTimeSelected) {
      form.setValue('timeFrom', null);
      form.setValue('timeTo', null);
    } else if (!formSelectedTimeFrom) {
      form.setValue('timeFrom', parkingConfigurationFormDefaultValues.timeFrom);
      form.setValue('timeTo', parkingConfigurationFormDefaultValues.timeTo);
    }
  }, [unlimitedTimeSelected, formSelectedTimeFrom, form]);

  return {
    selectedCitiesCustomValue,
    selectedZonesCustomValue,
    selectedDaysCustomValue,
    unlimitedTimeSelected,
    selectedCitiesOptions,
    timeFromOptions,
    citiesOptions,
    timeToOptions,
    zonesOptions,
    daysOptions,
    sortCities,
    sortDays,
    form,
  };
};
